'use client';

/**
 * Free-text tag input — type, press Enter (or comma), get a chip.
 *
 * Built for the position form's skill / qualification lists, where the values
 * are open-vocabulary (a tenant's skill taxonomy is never complete) but the
 * stored shape must be a clean string[] rather than one comma-joined blob.
 *
 * De-dupes case-insensitively on add, since "React" and "react" are the same
 * skill to every downstream reader (matcher, blueprint, embedding).
 */

import { useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export function TagInput({
  id,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  'aria-describedby': ariaDescribedBy,
}: {
  id?: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-describedby'?: string;
}) {
  const [draft, setDraft] = useState('');

  const commit = (raw: string) => {
    const next = raw.trim();
    if (!next) return;
    // Case-insensitive de-dupe — keep the FIRST spelling the user entered.
    if (value.some((v) => v.toLowerCase() === next.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...value, next]);
    setDraft('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      // Enter inside a <form> would submit it; the tag is the intent here.
      e.preventDefault();
      commit(draft);
      return;
    }
    // Backspace on an empty draft removes the last chip — standard chip-input
    // affordance, and the only way to undo without reaching for the mouse.
    if (e.key === 'Backspace' && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const remove = (tag: string) => onChange(value.filter((v) => v !== tag));

  return (
    <div className={cn('space-y-2', className)}>
      <Input
        id={id}
        value={draft}
        disabled={disabled}
        placeholder={placeholder}
        aria-describedby={ariaDescribedBy}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        // Don't silently drop a half-typed tag when focus leaves the field.
        onBlur={() => commit(draft)}
      />
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <Badge key={tag} variant="secondary" className="gap-1 pr-1">
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(tag)}
                  aria-label={`Remove ${tag}`}
                  className="rounded-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <X className="w-3 h-3" aria-hidden="true" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
